
import { Component, EnvironmentInjector, inject, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, NavigationError, Router } from '@angular/router';
import { register } from 'swiper/element';
import { distinctUntilChanged, filter, firstValueFrom, map, Subscription } from 'rxjs';
import { AuthService } from './shared/services/auth.service';
import { collection, FBaseService } from './shared/services/firebase/f-base.service';
import { StorageItems } from './shared/constants/storage-items.constants';
import { PrevRouteService } from './shared/services/misc/prev-route.service';
import { LoggerService } from './shared/services/logger.service';
import { NetworkStatusService } from './shared/services/network-status.service';
import { WarningNoticeComponent } from './shared/components/warning-notice/warning-notice.component';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, WarningNoticeComponent],
})
export class AppComponent implements OnDestroy {
  public environmentInjector = inject(EnvironmentInjector);
  readonly networkStatus = inject(NetworkStatusService);

  private navigationErrorSub?: Subscription;
  private lastSelectedSubjectSub?: Subscription;

  constructor(
    private prevRouteService: PrevRouteService,
    private authService: AuthService,
    private logger: LoggerService,
    private firebaseService: FBaseService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    this.prevRouteService.loadRouting();
    this.navigationErrorSub = this.router.events.pipe(
      filter(e => e instanceof NavigationError),
      map(e => e as NavigationError)
    ).subscribe(e => {
      if (e.error instanceof Error && e.error.name == 'ChunkLoadError') {
        console.log("CHUNK LOAD RECOVER")
        window.location.assign(e.url);
      }
    });

    try {
      const user = await firstValueFrom(this.authService.currentUser());

      if (user && user.uid) {
        const fbuser = await firstValueFrom(this.firebaseService.getById(collection.users, user.uid));

        if (fbuser && (fbuser.role === 'TEACHER' || fbuser.role === 'ADMINISTRATOR')) {
          this.logger.storageSet(StorageItems.USER, user);
          this.logger.setEmail(user.email);
          this.logger.setUserId(user.uid);
        }
        if (fbuser && fbuser.role !== 'ADMINISTRATOR') {
          this.logger.storageRemove(StorageItems.USER_ID_FOR_ADMIN)
        }
      }
    } catch (error) {
      console.error('Error initializing app with user data:', error);
      await this.authService.logout();
    }

    this.setLastSelectedSubject();
  }

  setLastSelectedSubject() {
    this.lastSelectedSubjectSub = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let snapshot = this.route.snapshot;
          while (snapshot.firstChild) {
            snapshot = snapshot.firstChild;
          }
          const subjectId = snapshot.params['subjectId'];
          return subjectId;
        }),
        distinctUntilChanged()
      )
      .subscribe(subjectId => {
        if (subjectId) {
          localStorage.setItem(StorageItems.LAST_SELECTED_SUBJECT, subjectId);
        }
      });
  }

  ngOnDestroy() {
    this.navigationErrorSub?.unsubscribe();
    this.lastSelectedSubjectSub?.unsubscribe();
  }
}
